'use client'

import type { Country, CountryNameKey } from './country-data'
import { Check } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import React, { forwardRef, useEffect, useRef, useState } from 'react'

import { cn } from '~/lib/utils'
import { Button } from '~/modules/common/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/modules/common/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/modules/common/components/ui/popover'

import { ExpandIcon } from '~/modules/common/icons/expand'
import {
  getCountriesWithTranslations,
  getCountryByDialCode,
  getDefaultCountry,
} from './country-data'

interface CountrySelectorProps {
  value: string
  onChange: (value: string) => void
  className?: string
  mode?: 'dialCode' | 'code' | 'name'
  /** ISO 3166-1 alpha-2 country codes to restrict selection (only applies when mode !== 'dialCode') */
  allowedCountryCodes?: string[]
  countries?: { code: string, name: string }[]
}

// Normalize text by removing accents and converting to lowercase
function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .toLowerCase()
}

// Default fallback — kept in sync with backend ALLOWED_SHIPPING_COUNTRIES
const DEFAULT_ALLOWED_COUNTRY_CODES = ['ES', 'PT', 'GB', 'AD']

function getFlagFromCountryCode(code: string): string {
  const normalizedCode = code.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(normalizedCode)) {
    return ''
  }

  return String.fromCodePoint(
    ...Array.from(normalizedCode).map(character => 127397 + character.charCodeAt(0)),
  )
}

export const CountrySelector = forwardRef<
  HTMLButtonElement,
  CountrySelectorProps
>(
  (
    {
      value,
      onChange,
      className,
      mode = 'dialCode',
      allowedCountryCodes,
      countries: customCountries,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false)
    const selectedItemRef = useRef<HTMLDivElement | null>(null)
    const locale = useLocale()
    const t = useTranslations('common')
    const translateCountry = (
      key: CountryNameKey,
      values?: { defaultValue?: string },
    ) => t(key, values)

    // Get countries with translations. When the backend provides the list
    // of allowed countries, keep that list but enrich it with localized names
    // and flags instead of rendering the backend's raw English labels.
    let countries = getCountriesWithTranslations(translateCountry)
    if (customCountries?.length) {
      const translatedCountriesByCode = new Map(
        countries.map(country => [country.code, country]),
      )
      const regionNames = new Intl.DisplayNames([locale], { type: 'region' })

      countries = customCountries.map((country) => {
        const code = country.code.trim().toUpperCase()
        const translatedCountry = translatedCountriesByCode.get(code)

        return {
          code,
          name:
            translatedCountry?.name
            ?? regionNames.of(code)
            ?? country.name,
          dialCode: translatedCountry?.dialCode ?? '',
          flag: translatedCountry?.flag ?? getFlagFromCountryCode(code),
        }
      })
    }

    // Filter allowed countries for address selection (not for phone dial codes)
    if (mode !== 'dialCode' && !customCountries?.length) {
      const codes = allowedCountryCodes ?? DEFAULT_ALLOWED_COUNTRY_CODES
      countries = countries.filter(c => codes.includes(c.code))
    }

    // Get current selected country. In code mode we also accept legacy
    // values stored as localized/raw names so the selector does not fall back
    // to Spain before AdminAddressFields normalizes them to an ISO code.
    const normalizedValue = normalizeText(value.trim())
    const legacyCustomCountryCode = customCountries?.find(country =>
      normalizeText(country.name) === normalizedValue,
    )?.code
    let selectedCountry: Country | undefined
    if (mode === 'dialCode') {
      selectedCountry = getCountryByDialCode(value, translateCountry)
    }
    else {
      selectedCountry = countries.find((country) => {
        if (mode === 'name') {
          return normalizeText(country.name) === normalizedValue
        }

        return country.code.toUpperCase() === value.trim().toUpperCase()
          || normalizeText(country.name) === normalizedValue
          || country.code === legacyCustomCountryCode
      }) || getDefaultCountry(translateCountry)
    }

    if (!selectedCountry) {
      // fallback if not found
    }

    useEffect(() => {
      if (!open || !selectedCountry?.code) {
        return
      }

      const frameId = window.requestAnimationFrame(() => {
        selectedItemRef.current?.scrollIntoView({
          block: 'center',
          inline: 'nearest',
        })
      })

      return () => window.cancelAnimationFrame(frameId)
    }, [open, selectedCountry?.code])

    // Custom filter function that normalizes text for accent-insensitive search
    // Searches in country name, dial code, and country code
    const filterFunction = (countryValue: string, search: string) => {
      // countryValue is in format "name|dialCode|code" (we'll set this in CommandItem)
      const [name, dialCode, code] = countryValue.split('|')
      const normalizedSearch = normalizeText(search)

      // Search in name, dial code, or country code
      return normalizeText(name || '').includes(normalizedSearch)
        || dialCode?.includes(search)
        || normalizeText(code || '').includes(normalizedSearch)
        ? 1
        : 0
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'flex gap-3 justify-between items-center px-6 py-3 w-full text-sm leading-none rounded-xl border border-black border-solid h-[50px] bg-white hover:bg-gray-50 transition-colors',
              className,
            )}
          >
            <div className="flex items-center gap-3 truncate">
              {selectedCountry?.flag ? (
                <span className="text-lg">{selectedCountry.flag}</span>
              ) : null}
              <span className="text-stone-900 truncate">
                {mode === 'dialCode'
                  ? `${selectedCountry?.name} [${selectedCountry?.dialCode}]`
                  : selectedCountry?.name}
              </span>
            </div>
            <ExpandIcon />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command filter={filterFunction}>
            <CommandInput
              placeholder={t('cart.searchCountry', {
                defaultValue: 'Buscar país...',
              })}
            />
            <CommandList>
              <CommandEmpty>
                {t('cart.noCountriesFound', {
                  defaultValue: 'No se encontraron países',
                })}
              </CommandEmpty>
              <CommandGroup>
                {countries.map(country => (
                  <CommandItem
                    key={country.code}
                    ref={country.code === selectedCountry?.code ? selectedItemRef : undefined}
                    value={`${country.name}|${country.dialCode}|${country.code}`}
                    onSelect={() => {
                      if (mode === 'code') {
                        onChange(country.code)
                      }
                      else if (mode === 'name') {
                        onChange(country.name)
                      }
                      else {
                        onChange(country.dialCode)
                      }
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        selectedCountry?.code === country.code
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                    {country.flag ? (
                      <span className="text-lg mr-2">{country.flag}</span>
                    ) : null}
                    <div className="flex-1 min-w-0 flex items-center justify-between">
                      <span className="text-sm font-medium text-stone-900 truncate">
                        {country.name}
                      </span>
                      {mode === 'dialCode' && (
                        <span className="text-sm text-stone-600 ml-2">
                          {country.dialCode}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

CountrySelector.displayName = 'CountrySelector'
